import { useState, useEffect, useContext, useRef } from "react"
import { useLocation } from "react-router-dom"
import { Formik, useField } from "formik"
import { useParams } from "react-router-dom"
import { DateTime } from "luxon"

//graphQL
import { useLazyQuery, useQuery } from "@apollo/client"
import {
  REFERRAL_BY_IDSTAMP,
  REFERRAL_EXTRA_DETAIL,
  GET_MAX_SERVICE_EPISODE,
} from "./query"

// Styles
import styles from "../../styles/Page-with-Sidebar.module.css"
import refStyles from "../../styles/Referrals.module.css"
import formStyles from "../../styles/Form-Inputs.module.css"

//Referal modules
import { RefApiRefEntityStatusGet, RefApiRefRatingGet } from "./ReferralsAPI"
import RefTextInput from "./RefTextInput"
import { TabPanel, TabView } from "primereact/tabview"
import ReferralNotes from "./ReferralNotes"
import { getLocalDateTimeFromIso } from "./sharedFunctions"
import ReferralRating from "./ReferralRating"
import ReferralStatusPopup from "./ReferralStatusPopup"

// Prime React
import { Button } from "primereact/button"
import { Editor as InputTextareaRich } from "primereact/editor"

// Context
import { UserContext } from "../../context/userContext"

// Custom components and helpers
import { LogError } from "../../helpers/logger"
import AppBreadcrumb from "../../components/AppBreadcrumb/AppBreadcrumb"
import PrevBreadLink from "../../components/AppBreadcrumb/PrevBreadLink"
import LoaderFullPage from "../../components/Loaders"
import CustomRating from "./CustomRating"
import ReferralParticipantNote from "./ReferralParticipantNote"
import RefTextAreaInput from "./RefTextAreaInput"
import { getReferralsParticipantServiceStaffId } from "../../config/microservicesSystemUsers"

export default function ReferralsDetail() {
  const { id } = useParams()
  const userCtx = useContext(UserContext)
  const location = useLocation()
  const topRef = useRef(null)

  const [errorMessage, setErrorMessage] = useState("")
  const [referral, setReferral] = useState()
  const [refStatus, setRefStatus] = useState()
  const [refRating, setRefRating] = useState(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingRefStatus, setIsLoadingRefStatus] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [activeIndex, setActiveIndex] = useState(0)
  const [thisNoteSet, setThisNoteSet] = useState(
    referral?.referral.values.referral_participants_notes,
  )
  const [serviceEpisode, setServiceEpisode] = useState(false)
  //ReferralStatusUpdate
  const [showRefStatusUpdate, setShowRefStatusUpdate] = useState(false)

  const successfulReferralStatus = "Completed referral"
  const blockNameStaff = "staff"

  const [getReferralColumns] = useLazyQuery(REFERRAL_EXTRA_DETAIL, {
    fetchPolicy: "cache-and-network",
    variables: {
      organization_id: "",
    },
  })

  useEffect(() => {
    if (referral) {
      // Fetch the max service episode when referral or userCtx changes
      getMaxServiceEpisode({
        variables: {
          staff_id: getReferralsParticipantServiceStaffId(),
          organization_id: referral?.referral?.organization_id || "",
          participant_id: userCtx.participant_id,
          participant_membership_id:
            referral?.referral?.participant_membership_id || "",
        },
      })
    }

    // Set the participant notes if referral changes
    setThisNoteSet(referral?.referral.values.referral_participants_notes)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referral])

  useQuery(REFERRAL_BY_IDSTAMP, {
    variables: { idStamp: id },
    fetchPolicy: "network-only",
    onCompleted: async (result) => {
      try {
        if (result.referrals !== null) {
          const referralsParsed = JSON.parse(result.referrals.data)

          let referralDetail = undefined
          let referralSession = undefined
          if (referralsParsed.length > 0) {
            referralDetail = referralsParsed.find(
              (block) => block.block_name === "referral",
            )
            referralSession = referralsParsed.find(
              (block) => block.block_name === "session",
            )

            if (!!referralSession) {
              const sessionDate = DateTime.fromISO(
                referralSession.values.session_date,
              )
                .toLocal()
                .toLocaleString()

              referralSession.values.session_date_local = sessionDate
            }
          }

          const referral_extra = await getReferralColumns({
            variables: {
              organization_id: referralDetail.organization_id,
            },
          })

          if (!referral_extra.data.referral_block) {
            throw new Error("Unable to retrieve referral columns")
          }

          const refEntity = { status: "loading..." }

          if (referralDetail !== undefined) {
            //Fix dates
            referralDetail.values.referral_appointment_date =
              getLocalDateTimeFromIso(
                referralDetail.values.referral_appointment_date,
                "MM/dd/yyyy",
              )
            referralDetail.values.referral_appointment_time =
              getLocalDateTimeFromIso(
                referralDetail.values.referral_appointment_time,
                "HH:mm",
              )

            setReferral({
              referral: referralDetail,
              referral_session: referralSession,
              refEntity: refEntity,
              referral_block: referral_extra.data.referral_block,
              organization_name:
                referralsParsed.find((x) => x.block_name === blockNameStaff)
                  ?.organization_name || "Unknown Organization",
            })

            let refPromises = []

            refPromises.push(
              RefApiRefEntityStatusGet(userCtx.tokens.getMainToken(), id)
                .then((result) => {
                  setRefStatus(result)
                })
                .catch((ex) => {
                  console.log("ex", ex)

                  if (ex.isAxiosError && ex.response !== undefined) {
                    setErrorMessage(ex.response.data)
                  } else {
                    setErrorMessage("Referral API: " + ex.message)
                  }
                }),
            )

            refPromises.push(
              RefApiRefRatingGet(userCtx.tokens.getMainToken(), id)
                .then((result) => {
                  if (result.id === 0) {
                    result.participantId = referralDetail.participant_id
                    result.referralEntityId =
                      referralDetail.values.referral_entity_id
                    result.referralId = id

                    result.isSatisfactionRating =
                      referralDetail.values.referral_status ===
                      successfulReferralStatus
                  }

                  setRefRating(result)
                })
                .catch((ex) => {
                  LogError("Unable to retrieve Referral rating", ex)

                  if (ex.isAxiosError && ex.response !== undefined) {
                    setErrorMessage(ex.response.data)
                  } else {
                    setErrorMessage("Referral API: " + ex.message)
                  }
                }),
            )

            //Wait for the Referral API calls to complete before continuing
            await Promise.all(refPromises).then((result) => {
              setIsInitialLoad(false)
              setIsLoading(false)
            })
          } else {
            setReferral(null)
            setIsInitialLoad(false)
            setIsLoading(false)
          }
        } else {
          setReferral(null)
          setIsInitialLoad(false)
          setIsLoading(false)
        }
      } catch (ex) {
        setErrorMessage(ex)
        setIsInitialLoad(false)
        setIsLoading(false)
      }
    },
    onError: (error) => {
      setErrorMessage(error.message)
      setIsInitialLoad(false)
      setIsLoading(false)
    },
  })

  const [getMaxServiceEpisode] = useLazyQuery(GET_MAX_SERVICE_EPISODE, {
    fetchPolicy: "network-only",
    onCompleted: ({ getMaxServiceEpisode }) => {
      try {
        const maxService = getMaxServiceEpisode
          .filter((r) => r.episode_intake_date)
          .sort((a, b) => {
            return a.episode_intake_date - b.episode_intake_date
          })
        if (maxService && maxService?.length > 0) {
          setServiceEpisode(maxService[0])
        }
      } catch (ex) {
        console.log(ex)
      }
    },
  })

  useEffect(() => {
    if (
      referral !== undefined &&
      refStatus !== undefined &&
      isLoadingRefStatus
    ) {
      setIsLoadingRefStatus(false)
      if (refStatus.referralId !== null) {
        setReferral({
          ...referral,
          refEntity: { status: refStatus.referralEntityStatus },
        })
      } else {
        setReferral({
          ...referral,
          refEntity: { status: "Pending" },
        })
      }
    }
  }, [referral, refStatus, isLoadingRefStatus])

  function NotesInput() {
    const [field] = useField("referral.values.referral_notes")

    if (isLoading || isInitialLoad) {
      return null
    }

    return (
      <div className={styles.field}>
        <label className={formStyles.label}>Referal notes</label>
        <div className="col-12 md:col-10 w-full pl-0">
          <InputTextareaRich
            name="referral.values.referral_notes"
            value={field.value}
            readOnly
            showHeader={false}
            className={`${formStyles.block} ${formStyles.noteBlock}`}
            style={{ height: "280px" }}
          />
        </div>
      </div>
    )
  }

  function getReferralDetail(values) {
    let excludeFields = [
      "referral_status",
      "referral_type",
      "referral_type_other",
      "referral_entity_name",
      "referral_entity_id",
      "referral_reason",
      "referral_participants_notes",
      "referral_notified_name",
      "referral_notified_email",
      "referral_notified_phone",
    ]

    const GetField = ({
      fieldPath,
      fieldName,
      fieldLabel,
      ignoreExclusion,
    }) => {
      if (
        !ignoreExclusion &&
        excludeFields.some((field) => field === fieldName)
      ) {
        return null
      }

      if (!!values) {
        const field = values.referral_block.fields.filter(
          (field) => field.idField === fieldName,
        )

        if (field.length > 0 && field[0].field_type !== "hidden") {
          let label =
            field[0].field_label !== "" ? field[0].field_label : fieldLabel

          //Replace Referral entity (case insensitive) with Referral partner
          label = label.replace(/Referral entity/i, "Referral partner")

          return (
            <RefTextInput
              name={fieldPath}
              label={label}
              placeholderText=""
              isInitialLoad={isInitialLoad}
              isLoading={isLoading}
              disabled={true}
            />
          )
        } else {
          return null
        }
      } else {
        return (
          <RefTextInput
            name={fieldName}
            label={fieldLabel}
            placeholderText=""
            isInitialLoad={isInitialLoad}
            isLoading={isLoading}
            disabled={true}
          />
        )
      }
    }

    return (
      <TabPanel header="Referral details" className="p-fluid">
        {!!refRating &&
          successfulReferralStatus ===
            referral?.referral.values.referral_status &&
          refRating.score > 0 &&
          activeIndex === 0 && (
            <div className={`${refStyles.tabContainer} mb-5 mt-1`}>
              <CustomRating
                isSubmitting={false}
                name={"score"}
                values={refRating}
              />
            </div>
          )}
        {referral?.referral.values.referral_status === "Incomplete referral" &&
          thisNoteSet && (
            <div
              className={`${refStyles.tabContainer} mb-5 mt-4`}
              style={{ display: "flex", flexDirection: "column" }}
            >
              <label
                className={`${refStyles.feedbackLabel} mb-3`}
              >{`Participant feedback`}</label>
              <div>
                <RefTextAreaInput
                  name="referral.values.referral_participants_notes"
                  label="Why did you not complete this referral?"
                  style={{ width: "100%" }}
                  disabled={true}
                  value={thisNoteSet}
                />
              </div>
            </div>
          )}
        <div className={refStyles.tabContainer}>
          <RefTextInput
            name="organization_name"
            label="Organization"
            placeholderText="Organization name"
            isInitialLoad={isInitialLoad}
            disabled={true}
          />

          <RefTextInput
            name="refEntity.status"
            label="Referral partner status"
            placeholderText="Referral partner status"
            isInitialLoad={isInitialLoad}
            disabled={true}
          />

          <div
            style={{
              display: "flex",
              flex: "row",
              width: "100%",
            }}
          >
            <div style={{ width: "100%" }}>
              <GetField
                fieldPath="referral.values.referral_status"
                fieldName="referral_status"
                fieldLabel="Referral partner status"
                ignoreExclusion={true}
              />
            </div>

            {!isInitialLoad ? (
              <div>
                <Button
                  label="Update status"
                  type="button"
                  className={refStyles.updateButton}
                  style={{ top: "24px", marginRight: "20px" }}
                  onClick={() => {
                    setShowRefStatusUpdate(true)
                  }}
                  loading={isLoading || isInitialLoad}
                  icon="pi pi-sync"
                  disabled={
                    !allowStatusUpdate() &&
                    successfulReferralStatus ===
                      referral?.referral.values.referral_status
                  }
                />
              </div>
            ) : null}
          </div>

          <RefTextInput
            name="referral_session.values.session_date_local"
            label="Referral date"
            placeholderText=""
            isInitialLoad={isInitialLoad}
            disabled={true}
          />

          <GetField
            fieldPath="referral.values.referral_type"
            fieldName="referral_type"
            fieldLabel="Referral type"
            ignoreExclusion={true}
          />

          <GetField
            fieldPath="referral.values.referral_type_other"
            fieldName="referral_type_other"
            fieldLabel="Referral type other"
            ignoreExclusion={true}
          />
          <GetField
            fieldPath="referral.values.referral_reason"
            fieldName="referral_reason"
            fieldLabel="Referral reason"
            ignoreExclusion={true}
          />
          <GetField
            fieldPath="referral.values.referral_entity_name"
            fieldName="referral_entity_name"
            fieldLabel="Referral Partner"
            ignoreExclusion={true}
          />

          <NotesInput />
          {values?.referral_block.fields
            .filter((field) => field.field_name !== "referral_notes")
            .map((field) => (
              <GetField
                fieldPath={`referral.values.${field.idField}`}
                fieldName={field.idField}
                fieldLabel={field.field_label}
                key={field.idField}
              />
            ))}
        </div>
      </TabPanel>
    )
  }

  const scrollToTop = () => {
    topRef?.current?.scrollIntoView({ behavior: "smooth" })
  }

  const allowStatusUpdate = () => {
    const serviceDate = DateTime.fromISO(serviceEpisode.episode_intake_date)
    const referralDate = DateTime.fromSeconds(
      referral.referral.date_created._seconds,
    )
    return referralDate > serviceDate
  }

  if (isLoading) return <LoaderFullPage />

  return (
    <div className="main-content">
      <AppBreadcrumb
        items={[
          {
            label: "Custom Data Forms",
            template: (
              <PrevBreadLink
                previousRoute={location?.state?.previousRoute}
                previousTitle={location?.state?.previousTitle}
              />
            ),
          },
          { label: "View" },
        ]}
      />
      <ReferralRating
        rating={refRating}
        visible={
          !!refRating &&
          successfulReferralStatus ===
            referral?.referral.values.referral_status &&
          refRating.id === 0
        }
        setRefRating={setRefRating}
      />
      <ReferralParticipantNote
        referral={referral}
        thisNoteSet={thisNoteSet}
        setThisNoteSet={setThisNoteSet}
        visible={
          referral?.referral.values.referral_status === "Incomplete referral" &&
          !thisNoteSet
        }
      />
      <div className="w-full" ref={topRef}>
        <h1 className={"pageTitle"}>{location?.state?.previousTitle}</h1>
        <hr className={`line mb-4`} />
        {referral ? (
          <>
            <Formik
              initialValues={referral}
              enableReinitialize
              onSubmit={() => {}}
            >
              {({ values }) => (
                <>
                  <ReferralStatusPopup
                    allowStatusUpdate={allowStatusUpdate()}
                    showForm={showRefStatusUpdate}
                    onHideForm={() => {
                      setShowRefStatusUpdate(false)
                    }}
                    onStatusChanged={(newStatus) => {
                      setShowRefStatusUpdate(false)

                      let newRef = { ...referral }
                      newRef.referral.values.referral_status = newStatus
                      setReferral(newRef)
                    }}
                    referralIdStamp={referral?.referral.idStamp}
                    referralOrganizationId={referral?.referral.organization_id}
                    referralStatus={referral?.referral.values.referral_status}
                    referralEntityId={
                      referral?.referral.values.referral_entity_id
                    }
                    referralParticipantsNotes={
                      referral?.referral.values.referral_participants_notes
                    }
                  />
                  <TabView
                    activeIndex={activeIndex}
                    onTabChange={(e) => setActiveIndex(e.index)}
                  >
                    {getReferralDetail(values)}
                    <TabPanel header={"Notes"}>
                      {referral === undefined ? null : (
                        <div
                          className={`${refStyles.tabContainer} ${refStyles.notesContainer}`}
                        >
                          <ReferralNotes
                            referralId={id}
                            referralEntityId={
                              referral.referral.values.referral_entity_id
                            }
                            loadAllMessages={true}
                            referralCreatorStaffId={
                              referral?.referral?.staff_id || ""
                            }
                            referralParticipantId={
                              referral?.referral?.participant_id || ""
                            }
                            referralOrganizationID={
                              referral?.referral?.organization_id || ""
                            }
                          />
                        </div>
                      )}
                    </TabPanel>
                  </TabView>
                </>
              )}
            </Formik>
          </>
        ) : (
          <Formik>
            <TabView>{getReferralDetail(undefined)}</TabView>
          </Formik>
        )}
        <div className={refStyles.footer}>
          <Button
            label="Back to top"
            className={refStyles.backToTopBtn}
            onClick={scrollToTop}
          />
        </div>
      </div>
    </div>
  )
}
